import React, { useState } from "react";
import { YEARLY_SUBSCRIPTIONS } from "../constants/price-ids";

const pricingPlans = [
  {
    id: 1,
    name: "Starter",
    icon: "/heart-icon.svg",
    price: 19,
    price_id: "pri_01kevt774bak4n8qfs9fzvpwq1", //<-test mode
    // price_id: "pri_01kfg8qkk0mkqtsztzenq1rkxy", // <- live
    period: "/Per Month",
    description: "Best for freelancers getting started with smarter bidding.",
    buttonText: "Get Started!",
    features: [
      { text: "Manual bidding access", included: true },
      { text: "Proposal generation", included: true },
      { text: "Limited automated bids per day", included: true },
      { text: "Basic analytics", included: true },
      { text: "Email support", included: true },
      { text: "Priority support", included: false },
      { text: "Bid performance tracking", included: false },
    ],
  },
  {
    id: 2,
    name: "Standard",
    icon: "/star-icon.svg",
    price: 49,
    price_id: "pri_01kevw0n1mwvn4w20xtzmscr9q",
    period: "/Per Month",
    description: "Ideal for active freelancers handling multiple projects.",
    buttonText: "Get Started!",
    features: [
      { text: "Manual  + automated bidding", included: true },
      { text: "Higher daily bid limits", included: true },
      { text: "Advanced job matching", included: true },
      { text: "Bid performance tracking", included: true },
      { text: "Priority support", included: true },
      { text: "Advanced analytics dashboard", included: false },
      { text: "Dedicated support", included: false },
    ],
  },
  {
    id: 3,
    name: "Premium",
    icon: "/crown-icon.svg",
    price: 99,
    price_id: "pri_01kevw22e5ap5zh6edz8sgyc9x",
    period: "/Per Month",
    description: "Outstanding For professionals who want full bidding automation.",
    buttonText: "Get Started!",
    features: [
      { text: "Unlimited automated bidding", included: true },
      { text: "Custom bidding rules", included: true },
      { text: "Advanced analytics dashboard", included: true },
      { text: "Faster proposal processing", included: true },
      { text: "Dedicated support", included: true },
      { text: "Job Matching System", included: true },
      { text: "Manual  + automated bidding", included: true },
    ],
  },
];

export default function Pricing() {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <section className="py-20 mt-24  xl:mb-24 bg-light-blue-shade-20">
      <div className="h-16"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl 2xl:text-5xl font-gilroy font-extrabold heading-strong text-primary-blue mb-4">
            Pricing
          </h2>
          <p className="text-gray-600 text-lg 2xl:text-xl max-w-3xl mx-auto mb-8">
            Simple Plans for Every Freelancer. Switch between monthly and yearly billing anytime.
          </p>

          {/* Toggle Switch */}
          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${!isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none  focus:ring-offset-2 ${isYearly ? 'bg-primary-blue' : 'bg-gray-300'
                }`}
            >
              <span
                className={`${isYearly ? 'translate-x-9' : 'translate-x-1'
                  } inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-md`}
              />
            </button>
            <span className={`text-sm font-medium ${isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
              Yearly
            </span>
            <span className="ml-2 inline-block bg-blue-100 text-primary-blue text-xs font-semibold px-2 py-1 rounded-xl">
              Save 20%
            </span>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pricingPlans.map((plan) => (
            <PricingCard key={plan.id} plan={plan} isYearly={isYearly} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingCard({ plan, isYearly }) {
  const displayPrice = isYearly ? Math.floor(plan.price * 12 * 0.8) : plan.price;
  const price_id = isYearly ? YEARLY_SUBSCRIPTIONS[`${plan?.name?.toUpperCase()}`]["PRICE_ID"] : plan.price_id;
  const displayPeriod = isYearly ? "/Per Year" : plan.period;

  const checkout = () => {
    window.Paddle.Checkout.open({
      items: [{ priceId: price_id }],

      settings: {
        showAddDiscounts: false,
      }
    });
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300  border-t-4 border-primary-blue">
      <div className="flex justify-between">
        <div className="flex justify-center mb-4">
          <div className="">
            <img src={plan.icon} alt={plan.name} className="w-16 h-16" />
          </div>
        </div>

        {/* Price */}
        <div className="text-center mt-1 mb-6">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-4xl font-gilroy font-extrabold heading-strong text-primary-blue">${displayPrice}</span>
            <span className="text-gray-500 text-sm">{displayPeriod}</span>
          </div>
        </div>
      </div>
      {/* Plan Name */}
      <h3 className="text-xl font-bold text-gray-900 text-start mb-2">
        {plan.name}
      </h3>

      {/* Description */}
      <p className="text-gray-600 text-sm text-left mb-6">
        {plan.description}
      </p>

      {/* CTA Button */}
      <button onClick={checkout} className="w-[75%] bg-primary-blue text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary-blue/60 transition-colors duration-300 mb-6">
        {plan.buttonText}
      </button>

      {/* Features List */}
      <ul className="space-y-3">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <span className="flex-shrink-0 mt-1">
              <img
                src="/CheckBox.svg"
                alt="Included feature"
                className={`w-5 h-5 ${feature.included ? 'opacity-100' : 'opacity-30'}`}
              />
            </span>
            <span className={`text-sm ${feature.included ? 'text-gray-700' : 'text-gray-400 line-through opacity-40'}`}>
              {feature.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
